import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import socketService from '../services/socket';
import { TopicCategory, getTopicsByMode } from '../builtInDictionaries';
// Note: GameTypeSelection not used - competitive mode only supports one-to-one for now
import RoomBrowser from './RoomBrowser';
import JoinRequestPanel from './JoinRequestPanel';
import MyJoinRequests from './MyJoinRequests';
import '../styles/CompetitiveLobby.css';

interface Dictionary {
  id?: number;
  name: string;
  data: Record<string, string> | any; // Support all dictionary formats
  type: 'builtin' | 'uploaded' | 'saved';
  matching_mode?: 'one-to-one' | 'category' | 'sort' | 'multiple-choice';
}

interface Participant {
  id: number;
  user_id: number;
  username: string;
  ready: boolean;
  is_host: boolean;
  disconnected?: boolean;
}

interface Room {
  id: number;
  roomCode: string;
  hostUserId: number;
  dictionaryName: string;
  dictionaryData?: Record<string, string> | any;
  dictionaryType?: string;
  pairCount: number;
  maxPlayers: number;
  status: string;
  gameSeed: number;
  participants?: Participant[];
  matching_mode?: 'one-to-one' | 'category' | 'sort' | 'multiple-choice';
}

interface CompetitiveLobbyProps {
  onGameStart: (room: Room, isHost: boolean) => void;
  onBack: () => void;
  onProceedToTopics?: () => void;
  onRoomJoined?: (room: Room, participants: Participant[]) => void;
  topicCategories: TopicCategory[];
  existingRoom?: Room | null;
}

const CompetitiveLobby: React.FC<CompetitiveLobbyProps> = ({
  onGameStart,
  onBack,
  onProceedToTopics,
  onRoomJoined,
  topicCategories,
  existingRoom
}) => {
  const { user } = useAuth();
  const [mode, setMode] = useState<'select' | 'create' | 'join' | 'lobby'>('select');
  // Competitive mode only supports one-to-one (Pair Match) for now
  // Other modes (category, sort, multiple-choice) coming later
  const selectedGameType = 'one-to-one' as const;
  const [selectedDictionary, setSelectedDictionary] = useState<Dictionary | null>(null);
  const [pairCount, setPairCount] = useState(6);
  const [maxPlayers, setMaxPlayers] = useState(10);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [roomCode, setRoomCode] = useState('');
  const [currentRoom, setCurrentRoom] = useState<Room | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [readyCountdown, setReadyCountdown] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [disconnectedUsers, setDisconnectedUsers] = useState<Set<number>>(new Set());
  const [savedDictionaries, setSavedDictionaries] = useState<Dictionary[]>([]);
  const [loadingSavedDicts, setLoadingSavedDicts] = useState(false);

  // Track if we've already notified App.tsx to prevent infinite loop
  const hasNotifiedRef = React.useRef(false);

  const handlePlayerJoined = useCallback((data: any) => {
    console.log('🎉 Player joined event received:', data);
    setParticipants(data.participants);
    const room = data.room;
    setCurrentRoom(prev => prev ? { ...prev, ...room } : room);

    // Notify App.tsx that room has been joined if:
    // 1. It's the current user joining
    // 2. Either existingRoom is set (another round scenario) OR room is NOT in initial 'waiting' status (rejoin scenario)
    // 3. We haven't already notified (prevent infinite loop)
    const isRejoin = existingRoom || room.status !== 'waiting';
    console.log('🔍 Join analysis:', {
      userId: data.userId,
      currentUserId: user?.id,
      roomStatus: room.status,
      existingRoom: !!existingRoom,
      isRejoin,
      hasNotified: hasNotifiedRef.current,
      willNotify: onRoomJoined && room && data.userId === user?.id && isRejoin && !hasNotifiedRef.current
    });

    if (onRoomJoined && room && data.userId === user?.id && isRejoin && !hasNotifiedRef.current) {
      console.log('📢 Notifying App.tsx of room join (rejoin scenario)');
      hasNotifiedRef.current = true;
      onRoomJoined(room, data.participants);
    }

    // Start 30-second ready countdown for new non-host players
    if (data.userId !== room?.hostUserId && data.userId !== user?.id) {
      setReadyCountdown(30);
    }
  }, [user, onRoomJoined, existingRoom]);

  const handlePlayerLeft = useCallback((data: any) => {
    setParticipants(data.participants);
    if (data.room) {
      setCurrentRoom(prev => prev ? { ...prev, ...data.room } : data.room);
    }
  }, []);

  const handlePlayerReadyChanged = useCallback((data: any) => {
    console.log('✅ Player ready changed event received:', data);
    setParticipants(data.participants);
  }, []);

  const handleGameCountdown = useCallback((data: { countdown: number }) => {
    setCountdown(data.countdown);
  }, []);

  const handleGameStarted = useCallback((data: any) => {
    if (user) {
      // Use room data from event if available, otherwise fallback to currentRoom
      const room = data?.room || currentRoom;
      if (room) {
        const isHost = room.hostUserId === user.id;
        onGameStart(room, isHost);
      }
    }
  }, [currentRoom, user, onGameStart]);

  const handleSocketError = useCallback((data: { message: string }) => {
    setError(data.message);
  }, []);

  const handleProceedToTopics = useCallback(() => {
    console.log('🚀 Received proceed to topics event');
    if (onProceedToTopics) {
      onProceedToTopics();
    }
  }, [onProceedToTopics]);

  // Connect to Socket.IO when component mounts (once)
  useEffect(() => {
    if (!user) return;

    socketService.connect(user.id);

    return () => {
      // Only cleanup on unmount
      if (currentRoom && user) {
        socketService.leaveRoom(currentRoom.id, user.id);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only re-run if user changes

  // Register event listeners (update when handlers change)
  useEffect(() => {
    if (!user) return;

    socketService.on('player_joined', handlePlayerJoined);
    socketService.on('player_left', handlePlayerLeft);
    socketService.on('player_ready_changed', handlePlayerReadyChanged);
    socketService.on('game_countdown', handleGameCountdown);
    socketService.on('game_started', handleGameStarted);
    socketService.on('proceed_to_topics', handleProceedToTopics);
    socketService.on('error', handleSocketError);

    return () => {
      socketService.off('player_joined', handlePlayerJoined);
      socketService.off('player_left', handlePlayerLeft);
      socketService.off('player_ready_changed', handlePlayerReadyChanged);
      socketService.off('game_countdown', handleGameCountdown);
      socketService.off('game_started', handleGameStarted);
      socketService.off('proceed_to_topics', handleProceedToTopics);
      socketService.off('error', handleSocketError);
    };
  }, [user, handlePlayerJoined, handlePlayerLeft, handlePlayerReadyChanged, handleGameCountdown, handleGameStarted, handleProceedToTopics, handleSocketError]);

  // Initialize with existing room if provided (for "Start Another Round")
  // Use a ref to track if we've already joined to prevent infinite loop
  const hasJoinedRef = React.useRef(false);

  useEffect(() => {
    if (existingRoom && user && !hasJoinedRef.current) {
      console.log('🔄 Rejoining existing room:', existingRoom);
      hasJoinedRef.current = true;
      setCurrentRoom(existingRoom);
      setMode('lobby');

      // Rejoin the room via socket
      socketService.joinRoom(existingRoom.id, user.id, user.username);

      // Update stage to 'lobby'
      socketService.getSocket()?.emit('update_user_stage', {
        roomId: existingRoom.id,
        userId: user.id,
        username: user.username,
        stage: 'lobby'
      });

      // Fetch current participants
      apiService.get(`/api/competitive/room/${existingRoom.roomCode}`)
        .then(data => {
          if (data.participants) {
            setParticipants(data.participants);
          }
        })
        .catch(err => {
          console.error('Error fetching room participants:', err);
        });
    }
  }, [existingRoom, user]);

  // Load saved dictionaries when in create mode
  useEffect(() => {
    if (mode === 'create' && user && savedDictionaries.length === 0) {
      setLoadingSavedDicts(true);
      apiService.getDictionaries()
        .then((response: any) => {
          const dicts = Array.isArray(response) ? response : (response.dictionaries || []);
          const formattedDicts: Dictionary[] = dicts.map((dict: any) => ({
            id: dict.id,
            name: dict.name,
            data: dict.data || {},
            type: 'saved' as const,
            matching_mode: dict.matching_mode || 'one-to-one'
          }));
          setSavedDictionaries(formattedDicts);
        })
        .catch(err => {
          console.error('Failed to load saved dictionaries:', err);
        })
        .finally(() => {
          setLoadingSavedDicts(false);
        });
    }
  }, [mode, user, savedDictionaries.length]);

  // 30-second ready countdown timer
  useEffect(() => {
    if (readyCountdown === null || readyCountdown <= 0) return;

    const timer = setInterval(() => {
      setReadyCountdown(prev => {
        if (prev === null || prev <= 1) {
          // Countdown finished - mark non-ready users as disconnected
          setParticipants(prevParticipants =>
            prevParticipants.map(p => {
              if (!p.ready && !p.is_host) {
                setDisconnectedUsers(prevDisconnected => {
                  const newSet = new Set(prevDisconnected);
                  newSet.add(p.user_id);
                  return newSet;
                });
                return { ...p, disconnected: true };
              }
              return p;
            })
          );
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [readyCountdown]);

  // Reset countdown when player becomes ready
  useEffect(() => {
    const allReady = participants.every(p => p.ready || p.is_host);
    if (allReady) {
      setReadyCountdown(null);
    }
  }, [participants]);

  const handleCreateRoom = async () => {
    if (!selectedDictionary || !user) return;

    setLoading(true);
    setError('');

    try {
      // Detect matching mode from dictionary data
      const matchingMode = selectedDictionary.data?.metadata?.matchingMode === 'category' ? 'category' : 'one-to-one';

      const response = await apiService.post('/api/competitive/create-room', {
        dictionaryType: selectedDictionary.type,
        dictionaryData: selectedDictionary.data,
        dictionaryName: selectedDictionary.name,
        pairCount,
        dictionaryId: selectedDictionary.id,
        maxPlayers,
        matchingMode
      });

      const room = response.room;
      setCurrentRoom(room);
      setMode('lobby');

      // Join the room via Socket.IO
      socketService.joinRoom(room.id, user.id, user.username);

      // Update stage to 'lobby'
      socketService.getSocket()?.emit('update_user_stage', {
        roomId: room.id,
        userId: user.id,
        username: user.username,
        stage: 'lobby'
      });

      // Host is automatically ready
      setIsReady(true);

    } catch (err: any) {
      setError(err.message || 'Failed to create room');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = async () => {
    if (!roomCode || !user) return;

    setLoading(true);
    setError('');

    try {
      const response = await apiService.post('/api/competitive/join-room', {
        roomCode: roomCode.toUpperCase()
      });

      const room = response.room;
      setCurrentRoom(room);
      setMode('lobby');

      // Join the room via Socket.IO
      socketService.joinRoom(room.id, user.id, user.username);

      // Update stage to 'lobby'
      socketService.getSocket()?.emit('update_user_stage', {
        roomId: room.id,
        userId: user.id,
        username: user.username,
        stage: 'lobby'
      });

      // Get initial participants
      const roomDetails = await apiService.get(`/api/competitive/room/${roomCode.toUpperCase()}`);
      setParticipants(roomDetails.room.participants || []);

    } catch (err: any) {
      setError(err.message || 'Failed to join room');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReady = () => {
    if (!currentRoom || !user) return;

    // Hosts are always ready
    if (currentRoom.hostUserId === user.id) return;

    socketService.toggleReady(currentRoom.id, user.id);
    setIsReady(!isReady);
  };

  const handleStartGame = () => {
    if (!currentRoom || !user) return;

    // Check if all players are ready
    const allReady = participants.every(p => p.ready);
    if (!allReady) {
      setError('All players must be ready before starting');
      return;
    }

    if (participants.length < 2) {
      setError('Need at least 2 players to start');
      return;
    }

    socketService.startGame(currentRoom.id, user.id);
  };

  const handleLeaveRoom = () => {
    if (currentRoom && user) {
      socketService.leaveRoom(currentRoom.id, user.id);
      setCurrentRoom(null);
      setParticipants([]);
      setIsReady(false);
      setMode('select');
    }
  };

  const handleRemovePlayer = async (userId: number) => {
    if (!currentRoom || !user) return;

    // Only host can remove players
    if (currentRoom.hostUserId !== user.id) return;

    try {
      await apiService.post(`/api/competitive/remove-player`, {
        roomId: currentRoom.id,
        userId
      });

      // Remove from disconnected set
      setDisconnectedUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });

      // The player_left event will update participants list
    } catch (err: any) {
      setError(err.message || 'Failed to remove player');
    }
  };

  const getMaxPairs = (dict: Dictionary): number => {
    const keys = Object.keys(dict.data);
    const uniquePairs = new Set<string>();

    for (const key of keys) {
      const value = dict.data[key];
      const pairKey = [key, value].sort().join('|');
      uniquePairs.add(pairKey);
    }

    return uniquePairs.size;
  };

  // Mode: Select (Create or Join)
  if (mode === 'select') {
    return (
      <div className="competitive-lobby">
        <div className="lobby-header">
          <button onClick={onBack} className="back-button">← Back</button>
          <h2>🏆 Competitive Mode</h2>
        </div>

        <div className="mode-selection">
          <button
            className="mode-card create-mode"
            onClick={() => {
              // Skip game type selection - only one-to-one is supported in competitive mode
              setMode('create');
            }}
          >
            <div className="mode-icon">🎮</div>
            <h3>Create Room</h3>
            <p>Start a new game and invite friends</p>
          </button>

          <button
            className="mode-card join-mode"
            onClick={() => setMode('join')}
          >
            <div className="mode-icon">🚪</div>
            <h3>Join Room</h3>
            <p>Enter a room code to join</p>
          </button>
        </div>
      </div>
    );
  }

  // Mode: Create Room (Dictionary Selection)
  // Note: Only one-to-one (Pair Match) mode is supported in competitive mode for now
  if (mode === 'create') {
    return (
      <div className="competitive-lobby">
        <div className="lobby-header">
          <button onClick={() => setMode('select')} className="back-button">← Back</button>
          <h2>🔄 Pair Match - Create Room</h2>
        </div>

        <div className="create-room-form">
          <div className="form-section">
            <label>Select Game:</label>

            {/* Built-in Dictionaries Section - Filtered by Game Type */}
            <div className="dictionary-section">
              <h3 className="section-title">Built-in Games</h3>
              {getTopicsByMode(selectedGameType).map((category) => (
                <div key={category.id} className="category-section">
                  <div className="category-header">
                    <span className="category-icon">{category.icon}</span>
                    <h4 className="category-name">{category.name}</h4>
                    <span className="category-description">{category.description}</span>
                  </div>
                  <div className="category-grid">
                    {category.games.map((game) => {
                      const dict: Dictionary = {
                        name: game.name,
                        data: game.data,
                        type: 'builtin',
                        matching_mode: selectedGameType
                      };
                      return (
                        <button
                          key={game.id}
                          className={`dictionary-card ${selectedDictionary?.name === game.name ? 'selected' : ''}`}
                          onClick={() => setSelectedDictionary(dict)}
                        >
                          <div className="dict-name">{game.name}</div>
                          <div className="dict-pairs">{getMaxPairs(dict)} matches</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Saved Dictionaries Section */}
            <div className="dictionary-section saved-section">
              <div className="category-section">
                <div className="category-header">
                  <span className="category-icon">💾</span>
                  <h4 className="category-name">Saved Games</h4>
                  <span className="category-description">Your custom games</span>
                </div>
                {loadingSavedDicts ? (
                  <div className="empty-state">
                    <p>Loading your games...</p>
                  </div>
                ) : savedDictionaries.filter(dict => dict.matching_mode === 'one-to-one').length === 0 ? (
                  <div className="empty-state">
                    <p>No saved Pair Match games yet. Create one in free play mode!</p>
                  </div>
                ) : (
                  <div className="category-grid">
                    {savedDictionaries
                      .filter(dict => dict.matching_mode === 'one-to-one')
                      .map(dict => (
                        <button
                          key={dict.id}
                          className={`dictionary-card ${selectedDictionary?.id === dict.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedDictionary(dict);
                            setError('');
                          }}
                        >
                          <div className="dict-name">{dict.name}</div>
                          <div className="dict-pairs">{getMaxPairs(dict)} matches</div>
                        </button>
                      ))
                    }
                  </div>
                )}
              </div>
            </div>
          </div>

          {selectedDictionary && (
            <>
              <div className="form-section">
                <label>Number of Pairs: {pairCount}</label>
                <input
                  type="range"
                  min={3}
                  max={Math.min(getMaxPairs(selectedDictionary), 20)}
                  value={pairCount}
                  onChange={(e) => setPairCount(parseInt(e.target.value))}
                />
              </div>

              <div className="form-section">
                <label>Max Players:</label>
                <div className="max-players-selection">
                  <div className="max-players-input-group">
                    <input
                      type="number"
                      min="2"
                      max="999"
                      value={isUnlimited ? '' : maxPlayers}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value >= 2) {
                          setMaxPlayers(value);
                          setIsUnlimited(false);
                        }
                      }}
                      placeholder="Enter number (min 2)"
                      className="max-players-input"
                      disabled={isUnlimited}
                    />
                    <span className="input-hint">Specific number of players</span>
                  </div>
                  <button
                    className={`unlimited-btn ${isUnlimited ? 'active' : ''}`}
                    onClick={() => {
                      if (isUnlimited) {
                        // Disable unlimited, go back to manual input
                        setIsUnlimited(false);
                        setMaxPlayers(10); // Reset to default
                      } else {
                        // Enable unlimited
                        setIsUnlimited(true);
                        setMaxPlayers(100);
                      }
                    }}
                  >
                    ♾️ Unlimited
                  </button>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button
                className="create-room-button"
                onClick={handleCreateRoom}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Room'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Handler for when a join request is accepted and user auto-joins
  const handleAcceptedJoin = (room: Room, participants: Participant[]) => {
    console.log('🎊 Join request accepted, setting up lobby:', { room, participants });

    // Set local state
    setCurrentRoom(room);
    setParticipants(participants);
    setMode('lobby');
    setError('');

    // Notify parent (App.tsx) if callback provided
    if (onRoomJoined) {
      onRoomJoined(room, participants);
    }
  };

  // Mode: Join Room
  if (mode === 'join') {
    return (
      <>
        {/* My Join Requests - floats on top */}
        <MyJoinRequests onAcceptedJoin={handleAcceptedJoin} />

        <div className="competitive-lobby">
          <div className="lobby-header">
            <button onClick={() => setMode('select')} className="back-button">← Back</button>
            <h2>Join Game Room</h2>
          </div>

          <div className="join-room-form">
            <div className="form-section">
              <label>Enter Room Code:</label>
              <input
                type="text"
                className="room-code-input"
                placeholder="ABC123"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                maxLength={6}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              className="join-room-button"
              onClick={handleJoinRoom}
              disabled={loading || roomCode.length !== 6}
            >
              {loading ? 'Joining...' : 'Join Room'}
            </button>

            {/* Browse Active Rooms */}
            <RoomBrowser />
          </div>
        </div>
      </>
    );
  }

  // Mode: Lobby (Waiting room)
  if (mode === 'lobby' && currentRoom) {
    const isHost = user?.id === currentRoom.hostUserId;
    const allReady = participants.every(p => p.ready);

    return (
      <div className="competitive-lobby">
        {countdown !== null ? (
          <div className="countdown-overlay">
            <div className="countdown-number">
              {countdown > 0 ? countdown : 'GO!'}
            </div>
          </div>
        ) : (
          <>
            <div className="lobby-header">
              <button onClick={handleLeaveRoom} className="back-button">← Leave</button>
              <h2>Game Lobby</h2>
            </div>

            <div className="room-info">
              <div className="room-code-display">
                <span className="label">Room Code:</span>
                <span className="code">{currentRoom.roomCode}</span>
                <button
                  className="copy-code-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(currentRoom.roomCode);
                    // Show temporary success message
                    const btn = document.querySelector('.copy-code-btn');
                    if (btn) {
                      const originalText = btn.textContent;
                      btn.textContent = '✓ Copied!';
                      setTimeout(() => {
                        btn.textContent = originalText || '📋 Copy';
                      }, 2000);
                    }
                  }}
                  title="Copy room code to clipboard"
                >
                  📋 Copy
                </button>
              </div>
              <div className="room-details">
                <p><strong>Topic:</strong> {currentRoom.dictionaryName}</p>
                <p><strong>Matches:</strong> {currentRoom.pairCount}</p>
                <p><strong>Players:</strong> {participants.length}/{currentRoom.maxPlayers}</p>
              </div>
            </div>

            {/* Join Request Panel */}
            <JoinRequestPanel
              roomId={currentRoom.id}
              isInActiveGame={false}
            />

            <div className="participants-section">
              <h3>Players {readyCountdown !== null && <span className="ready-countdown">⏱ {readyCountdown}s</span>}</h3>
              <div className="participants-list">
                {participants.map(participant => (
                  <div
                    key={participant.id}
                    className={`participant-card ${participant.ready ? 'ready' : ''} ${participant.disconnected ? 'disconnected' : ''}`}
                  >
                    <div className="participant-info">
                      <span className="participant-name">
                        {participant.username}
                        {participant.is_host && <span className="host-badge">👑 Host</span>}
                        {participant.disconnected && <span className="disconnected-badge">⚠ Disconnected</span>}
                      </span>
                      <span className="ready-status">
                        {participant.disconnected ? '❌ Disconnected' : participant.ready ? '✓ Ready' : '⏳ Not Ready'}
                      </span>
                    </div>
                    {isHost && participant.disconnected && (
                      <button
                        className="remove-player-button"
                        onClick={() => handleRemovePlayer(participant.user_id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="lobby-actions">
              {!isHost && (
                <button
                  className={`ready-button ${isReady ? 'ready' : ''}`}
                  onClick={handleToggleReady}
                >
                  {isReady ? '✓ Ready' : 'Ready Up'}
                </button>
              )}

              {isHost && (
                <button
                  className="start-game-button"
                  onClick={handleStartGame}
                  disabled={!allReady || participants.length < 2}
                >
                  Start Game
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
};

export default CompetitiveLobby;
